<template>
  <page-wrapper :title="title">
    <appointment-filter-bar
      v-model="filterForm"
      @search="handleSearch"
    ></appointment-filter-bar>
    <p class="pl-5 pr-5 mb-2 mt-2" v-if="total">{{ totalCount }}</p>
    <appointment-table :data="list"></appointment-table>
    <div class="text-right mt-4" v-if="total">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :size="10"
        :current-page="queryParams.page"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import { Pagination } from "element-ui";
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import AppointmentTable from "../components/AppointmentTable";
import AppointmentFilterBar from "../components/AppointmentFilterBar";
import { DateTime } from "luxon";
import { baanRueJaiClient } from "@/dependencies";
import { getDurationAppointmentTimeText } from "@/lib/dateTime";
import { serviceType } from "@/lib/mapper";

export default {
  components: {
    PageWrapper,
    ScButton,
    AppointmentTable,
    AppointmentFilterBar,
    "el-pagination": Pagination
  },
  data() {
    const isProductPage = this.$route.query
      && this.$route.query.pageType === "PRODUCT"

    const title = isProductPage
      ? "รายการสินค้า"
      : "รายการนัดหมาย";
    const appointments = [];
    const now = DateTime.utc();
    const filterForm = {
      dateTimeRange: null,
      createdAtRange: null,
      provider: null,
      project: null,
      service: null,
      home: null,
      status: null,
      user: null
    };
    const total = 0;

    return {
      title,
      appointments,
      filterForm,
      total,
      isProductPage
    };
  },
  computed: {
    totalCount() {
      const limit = 10;
      const total = this.total.toLocaleString();
      const currentTotalItem = this.list.length;
      const isLastPage = limit > currentTotalItem;
      const currentPage = this.queryParams.page;
      const end = (isLastPage ? total : currentPage * limit).toLocaleString();
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString();

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`;
    },
    queryParams() {
      return {
        page: +this.$route.query.page || 1,
        createdAtFrom: this.$route.query.createdAtFrom,
        createdAtTo: this.$route.query.createdAtTo,
        dateTimeFrom: this.$route.query.dateTimeFrom,
        dateTimeTo: this.$route.query.dateTimeTo,
        homeId: this.$route.query.homeId,
        serviceId: this.$route.query.serviceId,
        providerId: this.$route.query.providerId,
        projectId: this.$route.query.projectId,
        status: this.$route.query.status,
        userId: this.$route.query.userId
      };
    },
    list() {
      return this.appointments.map(appointment => {
        const {
          id,
          date_time,
          status,
          service,
          provider,
          project,
          created_at,
          user,
          home,
          package_name,
          main_option,
          extra_option,
          date_time_end
        } = appointment;

        const appointmentDurationTime = getDurationAppointmentTimeText(
          date_time || null,
          date_time_end || null
        )

        return {
          id: id.toString(),
          dateTime: DateTime.fromISO(date_time).toUTC(),
          status: status,
          service: {
            id: service.id.toString(),
            name: service.name,
            shortDescription: service.short_description
          },
          provider: {
            name: provider.name
          },
          home: {
            address: `บ้านเลขที่ ${home.address_no}`,
            project: project.name,
            contact: {
              name: `${user.first_name} ${user.last_name}`,
              phoneNumber: user.mobile_number || "-"
            }
          },
          route: {
            name: "AppointmentDetailPage",
            params: { id }
          },
          createdAt: DateTime.fromISO(created_at).toUTC(),
          packageName: package_name,
          mainOption: main_option || "",
          extraOption: extra_option || "",
          appointmentDurationTime
        };
      });
    },
    queryBody() {
      const params = {
        page: this.queryParams.page
      };

      if (this.queryParams.dateTimeFrom && this.queryParams.dateTimeTo) {
        params.dateTime = {
          from: this.queryParams.dateTimeFrom,
          to: this.queryParams.dateTimeTo
        };
      }

      if (this.queryParams.homeId) {
        params.homeId = this.queryParams.homeId;
      }

      if (this.queryParams.createdAtFrom && this.queryParams.createdAtTo) {
        params.createdAt = {
          from: this.queryParams.createdAtFrom,
          to: this.queryParams.createdAtTo
        };
      }

      if (this.queryParams.projectId) {
        params.projectId = this.queryParams.projectId;
      }

      if (this.queryParams.providerId) {
        params.providerId = this.queryParams.providerId;
      }

      if (this.queryParams.serviceId) {
        params.serviceId = this.queryParams.serviceId;
      }

      if (this.queryParams.status) {
        params.status = this.queryParams.status;
      }

      if (this.queryParams.userId) {
        params.userId = +this.queryParams.userId;
      }

      params.type = this.$route.query && this.$route.query.pageType === serviceType.PRODUCT
        ? serviceType.PRODUCT
        : serviceType.SERVICE

      return params;
    }
  },
  created() {
    this.setup();
  },
  watch: {
    "$route.query"() {
      this.setupAppointments();
    }
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      this.setupAppointments();
    },
    async setupAppointments() {
      const { count, appointments } = await this.fetchAppointments();

      this.total = count;
      this.appointments = appointments;
    },
    async handleSearch() {
      const query = {};

      const {
        dateTimeRange,
        createdAtRange,
        provider,
        project,
        service,
        home,
        status,
        user
      } = this.filterForm;

      if (dateTimeRange) {
        query.dateTimeFrom = dateTimeRange.from.toISO();
        query.dateTimeTo = dateTimeRange.to.toISO();
      }

      if (home) {
        query.homeId = home.homeId;
      }

      if (createdAtRange) {
        query.createdAtFrom = createdAtRange.from.toISO();
        query.createdAtTo = createdAtRange.to.toISO();
      }

      if (project) {
        query.projectId = project.projectId;
      }

      if (provider) {
        query.providerId = provider.id;
      }

      if (service) {
        query.serviceId = service.id;
      }

      if (status) {
        query.status = status;
      }

      if (user) {
        query.userId = user.user_id;
      }

      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, ...query, page: 1 }
      };

      this.$router.replace(route);
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page }
      };

      this.$router.replace(route);
    },
    async fetchAppointments() {
      const params = this.queryBody;

      const response = await baanRueJaiClient.admin.subscriptions.appointments.list(
        params
      );

      return response.data.result;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.filter-bar {
  padding: $space;
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/appointments/pages/AppointmentListPage.vue